import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Gift, RotateCcw, Loader2, AlertTriangle } from "lucide-react";

const DEFAULT_REASON = "Beta tester goodwill — complimentary 90-day Pro membership for early testers.";

// One-click bulk comp for beta testers, gated to super-admin, rendered beside the membership
// summary on Admin → Users. NEVER a one-way door: (1) a dry-run preview shows the exact count
// and usernames it will touch (and who it skips because they're paying) BEFORE the commit, and
// (2) the whole batch can be undone in one action, restoring each account's prior membership.
export function BetaCompPanel({ onChanged }) {
  const [batches, setBatches] = useState([]);
  const [preview, setPreview] = useState(null); // {would_comp, would_skip_paying, ...}
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [undoTarget, setUndoTarget] = useState(null); // a batch
  const [undoReason, setUndoReason] = useState("");
  const [undoing, setUndoing] = useState(false);

  const loadBatches = useCallback(() => {
    api.get("/admin/membership/comp-beta-testers/batches")
      .then(({ data }) => setBatches(data.batches || []))
      .catch(() => setBatches([]));
  }, []);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const openPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/membership/comp-beta-testers/preview", { days: 90, tier: "elite" });
      setPreview(data);
      setReason(DEFAULT_REASON);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (reason.trim().length < 20) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/membership/comp-beta-testers", { days: 90, tier: "elite", reason: reason.trim() });
      toast.success(`Comped ${data.comped} account${data.comped === 1 ? "" : "s"} · skipped ${data.skipped_paying} paying`);
      setPreview(null);
      loadBatches();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setCommitting(false);
    }
  };

  const runUndo = async () => {
    if (!undoTarget || undoReason.trim().length < 20) return;
    setUndoing(true);
    try {
      const { data } = await api.post("/admin/membership/comp-beta-testers/undo", {
        batch_id: undoTarget.batch_id, reason: undoReason.trim(),
      });
      toast.success(`Undone · restored ${data.reverted} account${data.reverted === 1 ? "" : "s"}` +
        (data.skipped ? ` · skipped ${data.skipped} (changed since)` : ""));
      setUndoTarget(null);
      setUndoReason("");
      loadBatches();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setUndoing(false);
    }
  };

  const lastLive = batches.find((b) => !b.reverted);

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="beta-comp-panel">
      <button
        onClick={openPreview}
        disabled={loading}
        data-testid="comp-beta-open"
        className="inline-flex items-center gap-1.5 border border-volt/50 bg-volt/5 hover:bg-volt/10 text-volt font-mono uppercase text-[10px] px-3 py-1.5 transition-colors disabled:opacity-50">
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gift className="w-3.5 h-3.5" />}
        Comp beta testers · 90-day Pro
      </button>
      {lastLive && (
        <button
          onClick={() => { setUndoTarget(lastLive); setUndoReason(""); }}
          data-testid="comp-beta-undo-open"
          title={`Batch ${lastLive.batch_id} · ${lastLive.count} accounts · ${(lastLive.created_at || "").slice(0, 10)}`}
          className="inline-flex items-center gap-1.5 border border-sniper hover:border-red-500/60 text-secondary-foreground hover:text-red-400 font-mono uppercase text-[10px] px-3 py-1.5 transition-colors">
          <RotateCcw className="w-3.5 h-3.5" /> Undo last comp ({lastLive.count})
        </button>
      )}

      {/* PREVIEW + CONFIRM */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-lg bg-surface border-sniper" data-testid="comp-beta-modal">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-foreground flex items-center gap-2">
              <Gift className="w-5 h-5 text-volt" /> Comp beta testers
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <p className="font-mono text-[13px] text-foreground" data-testid="comp-beta-summary">
                This will comp <span className="text-volt font-bold" data-testid="comp-beta-count">{preview.count}</span> account{preview.count === 1 ? "" : "s"}, 90 days Pro
                {preview.refresh_count ? <span className="text-muted-foreground"> ({preview.new_count} new · {preview.refresh_count} refreshed)</span> : null}
                {". "}
                Skipping <span className="text-secondary-foreground font-bold" data-testid="comp-beta-skip-count">{preview.skip_count}</span> paying subscriber{preview.skip_count === 1 ? "" : "s"}.
              </p>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Will comp ({preview.count})</div>
                <div className="border border-sniper bg-inset max-h-36 overflow-y-auto p-2 flex flex-wrap gap-1" data-testid="comp-beta-comp-list">
                  {preview.would_comp.length === 0 && <span className="font-mono text-[11px] text-muted-foreground">No eligible accounts.</span>}
                  {preview.would_comp.map((u) => (
                    <span key={u.user_id} data-testid={`comp-beta-comp-${u.username}`}
                      className="font-mono text-[10px] px-1.5 py-0.5 border border-volt/30 text-volt/90"
                      title={u.email}>
                      @{u.username}{u.refresh ? " ↻" : ""}
                    </span>
                  ))}
                </div>
              </div>

              {preview.would_skip_paying.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Skipping — paying ({preview.skip_count})</div>
                  <div className="border border-sniper bg-inset max-h-24 overflow-y-auto p-2 flex flex-wrap gap-1" data-testid="comp-beta-skip-list">
                    {preview.would_skip_paying.map((u) => (
                      <span key={u.user_id} data-testid={`comp-beta-skip-${u.username}`}
                        className="font-mono text-[10px] px-1.5 py-0.5 border border-sniper text-secondary-foreground" title={u.email}>
                        @{u.username}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2 border border-volt/30 bg-volt/5 p-2">
                <AlertTriangle className="w-4 h-4 text-volt shrink-0 mt-0.5" />
                <p className="font-mono text-[10px] text-secondary-foreground leading-relaxed">
                  This runs against real accounts immediately. It is reversible — the whole batch can be undone in one click from the "Undo last comp" button.
                </p>
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Reason (audited, min 20 chars)</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="comp-beta-reason"
                  rows={2}
                  className="w-full mt-1 border border-sniper bg-inset text-foreground font-mono text-[12px] p-2 focus:border-volt outline-none" />
                <div className="text-right font-mono text-[10px] text-muted-foreground" data-testid="comp-beta-reason-count">{reason.trim().length}/20</div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setPreview(null)} data-testid="comp-beta-cancel"
                  className="border border-sniper hover:border-foreground text-secondary-foreground font-mono uppercase text-[11px] px-4 py-2 transition-colors">Cancel</button>
                <button onClick={commit} disabled={committing || reason.trim().length < 20 || preview.count === 0}
                  data-testid="comp-beta-confirm"
                  className="inline-flex items-center gap-1.5 bg-volt text-black hover:bg-volt/90 font-mono uppercase text-[11px] px-4 py-2 transition-colors disabled:opacity-40">
                  {committing && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Comp {preview.count} now
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* UNDO */}
      <Dialog open={!!undoTarget} onOpenChange={(o) => { if (!o) setUndoTarget(null); }}>
        <DialogContent className="max-w-md bg-surface border-sniper" data-testid="comp-beta-undo-modal">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-foreground flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-red-400" /> Undo comp batch
            </DialogTitle>
          </DialogHeader>
          {undoTarget && (
            <div className="space-y-3 text-sm">
              <p className="font-mono text-[12px] text-secondary-foreground">
                Restores <span className="text-foreground font-bold">{undoTarget.count}</span> account{undoTarget.count === 1 ? "" : "s"} to the membership they had before this batch
                (run {(undoTarget.created_at || "").slice(0, 10)} by @{undoTarget.created_by}).
                Accounts changed since the batch are left untouched.
              </p>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Reason (audited, min 20 chars)</label>
                <textarea value={undoReason} onChange={(e) => setUndoReason(e.target.value)} rows={2}
                  data-testid="comp-beta-undo-reason"
                  placeholder="Why are you undoing this comp batch?"
                  className="w-full mt-1 border border-sniper bg-inset text-foreground font-mono text-[12px] p-2 focus:border-red-500 outline-none" />
                <div className="text-right font-mono text-[10px] text-muted-foreground">{undoReason.trim().length}/20</div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setUndoTarget(null)} data-testid="comp-beta-undo-cancel"
                  className="border border-sniper hover:border-foreground text-secondary-foreground font-mono uppercase text-[11px] px-4 py-2 transition-colors">Cancel</button>
                <button onClick={runUndo} disabled={undoing || undoReason.trim().length < 20}
                  data-testid="comp-beta-undo-confirm"
                  className="inline-flex items-center gap-1.5 border border-red-500/60 bg-red-500/10 text-red-400 hover:bg-red-500/20 font-mono uppercase text-[11px] px-4 py-2 transition-colors disabled:opacity-40">
                  {undoing && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Undo {undoTarget.count} comp{undoTarget.count === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
